import { mergeDetailPages } from "@/lib/flag-store";
export type FlagScope = "global" | "org" | "classroom" | "user";
export type FlagType = "bool" | "int" | "enum" | "string";
export type StoredValue = boolean | number | string;

export interface StoreFlagDefinition {
  key: string;
  type: FlagType;
  default: StoredValue;
  kind: "release" | "ops" | "permission" | "config";
  scopes: FlagScope[];
  client_exposed: string[];
  services: string[];
  owner: string;
  description: string;
  ticket: string | null;
  expires_at: string | null;
  values: string[] | null;
  min: number | null;
  max: number | null;
  ui_only: boolean;
}

export interface FlagOverride {
  flag_key: string;
  scope: FlagScope;
  scope_id: string | null;
  value: StoredValue;
  version: number;
  updated_by?: { id: number; email: string };
  updated_at?: string;
  reason?: string;
  name?: string | null;
}

export interface FlagDetail {
  definition: StoreFlagDefinition;
  overrides: Record<FlagScope, FlagOverride[]>;
  next_cursor: string | null;
}

export interface FlagAuditEvent {
  flag_key: string;
  scope: FlagScope;
  scope_id: string | null;
  action: "SET" | "CLEAR";
  old_value: StoredValue | null;
  new_value: StoredValue | null;
  actor: { id: number; email: string };
  reason: string;
  at: string;
  batch_id?: string | null;
}

export interface Explained {
  context: { user_id: number; classroom_id: number | null; org_id: string | null };
  flags: Record<string, { value: StoredValue; source: "default" | FlagScope }>;
}

export class FlagStoreError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly current: FlagOverride | null = null,
  ) {
    super(message);
  }
}

const BASE = "/api/proxy/flag-store";
const MAX_DETAIL_PAGES = 50;

const scopeIdPath = (scope: FlagScope, scopeId: string | null): string =>
  scope === "global" ? "global" : encodeURIComponent(scopeId ?? "");

async function request<T>(path: string, init: RequestInit = {}, environment?: string): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(environment ? { "x-feature-flags-environment": environment } : {}),
    },
  });
  const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    const detail = body.detail;
    if (detail && typeof detail === "object") {
      const { message, current } = detail as { message?: string; current?: FlagOverride | null };
      throw new FlagStoreError(message || `Request failed (${response.status})`, response.status, current ?? null);
    }
    throw new FlagStoreError(typeof detail === "string" ? detail : `Request failed (${response.status})`, response.status);
  }
  return body as T;
}

export const flagStore = {
  definitions: () => request<{ flags: StoreFlagDefinition[]; environment: string }>("/definitions"),
  detail: (key: string, cursor?: string | null) =>
    request<FlagDetail>(
      `/flags/${encodeURIComponent(key)}?limit=1000${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
    ),
  detailAll: async (key: string): Promise<FlagDetail> => {
    const pages: FlagDetail[] = [];
    let cursor: string | null = null;
    do {
      const page: FlagDetail = await flagStore.detail(key, cursor);
      pages.push(page);
      cursor = page.next_cursor;
    } while (cursor && pages.length < MAX_DETAIL_PAGES);
    return mergeDetailPages(pages);
  },
  audit: (key: string, cursor?: string | null) =>
    request<{ events: FlagAuditEvent[]; next_cursor: string | null }>(
      `/flags/${encodeURIComponent(key)}/audit?limit=50${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
    ),
  explain: (userId: number, classroomId?: number, orgId?: string) => {
    const params = new URLSearchParams({ user_id: String(userId) });
    if (classroomId) params.set("classroom_id", String(classroomId));
    if (orgId) params.set("org_id", orgId);
    return request<Explained>(`/evaluate?${params}`);
  },
  blastRadius: (scope: FlagScope, scopeId: string | null) =>
    request<{ affected_users: number }>(
      `/blast-radius?${new URLSearchParams({ scope, scope_id: scope === "global" ? "global" : scopeId ?? "" })}`,
    ),
  set: (
    key: string,
    scope: FlagScope,
    scopeId: string | null,
    body: { value: StoredValue; expected_version: number | null; reason: string },
    environment: string,
  ) =>
    request<{ override: FlagOverride; previous: FlagOverride | null }>(
      `/flags/${encodeURIComponent(key)}/overrides/${scope}/${scopeIdPath(scope, scopeId)}`,
      { method: "PUT", body: JSON.stringify(body) },
      environment,
    ),
  clear: (
    key: string,
    scope: FlagScope,
    scopeId: string | null,
    body: { expected_version: number; reason: string },
    environment: string,
  ) =>
    request<{ previous: FlagOverride | null }>(
      `/flags/${encodeURIComponent(key)}/overrides/${scope}/${scopeIdPath(scope, scopeId)}/clear`,
      { method: "POST", body: JSON.stringify(body) },
      environment,
    ),
};
